import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { Dispatcher, formatDispatcherType } from "@/types/dispatcher";
import api from "@/services/api";
import { useDebounce } from "@/hooks/useDebounce";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Incident } from "@/types/incidents";

interface AssignFirstResponderDialogProps {
  open: boolean;
  onClose: () => void;
  onAssign: (dispatcherIDs: number[]) => Promise<void>;
  AssignedDispatchers?: any[];
  alert?: Incident;
}

export function AssignFirstResponderDialog({
  open,
  onClose,
  onAssign,
  AssignedDispatchers,
  alert
}: Readonly<AssignFirstResponderDialogProps>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [responders, setResponders] = useState<Dispatcher[]>([]);
  const [selectedResponders, setSelectedResponders] = useState<Set<number>>(
    new Set()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery);
  const fetchResponders = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get("/dispatchers", {
        params: {
          search: debouncedSearchQuery,
          locationID: alert?.locationID
        },
      });
      setResponders(data.data || []);
    } catch (error) {
      console.error("Error fetching responders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      responders.length > 0 &&
      AssignedDispatchers &&
      AssignedDispatchers?.length > 0
    ) {
      const newSelectedResponders = new Set(selectedResponders);
      AssignedDispatchers.forEach((responder) => {
        newSelectedResponders.add(responder.dispatcherID);
      });
      setSelectedResponders(newSelectedResponders);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responders]);

  useEffect(() => {
    if (open) {
      fetchResponders();
    } else {
      setSearchQuery("");
      setSelectedResponders(new Set());
      setResponders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, open]);

  const handleCheckboxChange = (dispatcherId: number) => {
    const newSelectedResponders = new Set(selectedResponders);
    if (newSelectedResponders.has(dispatcherId)) {
      newSelectedResponders.delete(dispatcherId);
    } else {
      newSelectedResponders.add(dispatcherId);
    }
    setSelectedResponders(newSelectedResponders);
  };

  const handleAssign = async () => {
    if (selectedResponders.size > 0) {
      try {
        setIsAssigning(true);
        await onAssign(Array.from(selectedResponders));
        setSelectedResponders(new Set());
        onClose();
      } catch (error) {
        console.error("Error assigning responders:", error);
      } finally {
        setIsAssigning(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-h4 dark:text-gray-200">Assign First Responders</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            placeholder="Search responders..."
            value={searchQuery}
            className="dark:text-gray-200"
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {(() => {
              if (isLoading) {
                return (
                  <div className="flex justify-center py-4">
                    <LoadingSpinner />
                  </div>
                );
              }
              if (responders.length > 0) {
                return responders.map((responder) => (
                  <button
                    type="button"
                    key={responder.dispatcherID}
                    className="w-full text-left flex items-center justify-between p-2 border rounded hover:bg-gray-50 dark:hover:bg-dark cursor-pointer transition-colors"
                    onClick={() => handleCheckboxChange(responder.dispatcherID)}
                  >
                    <span className="flex flex-row items-center gap-6">
                      <span className="flex flex-1 w-28 md:min-w-48 flex-col">
                        <span className="text-sm font-medium dark:text-gray-200">
                          {responder.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-200">
                          {responder.contactNumber}
                        </span>
                      </span>
                      <span className="flex-1 text-sm text-gray-500 dark:text-gray-200">
                        {formatDispatcherType(responder.dispatcherType)}
                      </span>
                    </span>
                    <Checkbox
                      checked={selectedResponders.has(responder.dispatcherID)}
                      onCheckedChange={() =>
                        handleCheckboxChange(responder.dispatcherID)
                      }
                      tabIndex={-1}
                    />
                  </button>
                ));
              }
              return (
                <div className="text-center py-4 text-gray-500 dark:text-gray-200">
                  No responders found
                </div>
              );
            })()}
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600 dark:text-gray-200">
              {selectedResponders.size} responder
              {selectedResponders.size === 1 ? "" : "s"} selected
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" className="dark:text-gray-200 dark:bg-dark" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                disabled={selectedResponders.size === 0 || isAssigning}
              >
                {isAssigning ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="small" color="white" />
                    <span>Assigning...</span>
                  </div>
                ) : (
                  "Assign Responders"
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
